import { PlusOutlined } from "@ant-design/icons"
import {
  Button,
  Card,
  Descriptions,
  Form,
  Modal,
  notification,
  Table
} from "antd"
import { AxiosError } from "axios"
import { IDisplayCompany, ICreateCompanyRequest } from "platform-client/types"
import React from "react"
import { useMutation, useQuery, useQueryClient } from "react-query"
import usePlatform from "contexts/platform/use-platform"
import { Link } from "react-router-dom"
import { BrokerForm, ICreateBrokerForm } from "../../../CreateBroker/BrokerForm"

type TableRecord = IDisplayCompany | { id: string; isEmpty: true }

export const Summary: React.FC<ISummary> = (props) => {
  const platform = usePlatform()
  const queryClient = useQueryClient()
  const [form] = Form.useForm()
  const [isModalVisible, setIsModalVisible] = React.useState(false)

  const getParentCompany = useQuery(
    ["getParentCompany", props.company?.parentCompanyID],
    () => platform.company.getCompany(props.company?.parentCompanyID ?? ""),
    {
      enabled: !!props.company?.parentCompanyID
    }
  )

  const getChildCompanies = useQuery(
    ["getChildCompanies", props.company?.id],
    () => platform.company.getChildCompanies(props.company?.id ?? ""),
    {
      enabled: !!props.company?.id
    }
  )

  const createOffice = useMutation(
    (request: ICreateCompanyRequest) => platform.company.createCompany(request),
    {
      onError: (error: AxiosError) => {
        notification.error({
          message: "Unable to create office, please try again."
        })
      },
      onSuccess: () => {
        notification.success({
          message: "Office created successfully"
        })
        setIsModalVisible(false)
        form.resetFields()
        queryClient.invalidateQueries(["getChildCompanies", props.company?.id])
      }
    }
  )

  const getStatusLabel = (status: number | string | undefined | null) => {
    if (status === undefined || status === null) return "-"
    if (status === 0 || status === "Enabled") return "Enabled"
    if (status === 1 || status === "Disabled") return "Disabled"
    return "-"
  }

  const handleOpenModal = () => {
    const enabledProducts =
      props.company?.enabledProducts?.map((item) => item.referenceID) || []
    form.setFieldsValue({
      parentCompanyID: props.company?.id,
      status: "Enabled",
      enabledProducts: enabledProducts
    })
    setIsModalVisible(true)
  }

  const handleCloseModal = () => {
    setIsModalVisible(false)
    form.resetFields()
  }

  const handleCreateOffice = (formData: ICreateBrokerForm) => {
    const requestBody: ICreateCompanyRequest = {
      name: formData.name,
      companyType: "Broker",
      brokerCanBindBusiness: false,
      contactName: {
        title: null,
        firstName: formData.firstName || null,
        lastName: formData.lastName || null
      },
      contactEmailAddress: formData.emailAddress || null,
      contactPhoneNumber: formData.phoneNumber || null,
      address: {
        line1: formData?.address?.line1 || null,
        line2: formData?.address?.line2 || null,
        line3: formData?.address?.line3 || null,
        locality: formData?.address?.locality || null,
        province: formData?.address?.province || null,
        postalCode: formData?.address?.postalCode || null,
        provinceReferenceID: formData?.address?.provinceReferenceID,
        uniqueReference: formData?.address?.uniqueReference || null,
        country: formData?.address?.country
          ? JSON.parse(formData?.address?.country)
          : { referenceID: null, text: null }
      },
      enabledProducts: formData.enabledProducts || [],
      abn: formData.abn || null,
      afsl: formData.afsl || null,
      status: formData.status || "Enabled",
      parentCompanyID: props.company?.id || null
    }

    createOffice.mutate(requestBody)
  }

  return (
    <>
      <Card style={{ marginBottom: "20px" }} loading={props.isLoading}>
        <Descriptions size="small" column={2} bordered>
          {props.company?.name && (
            <Descriptions.Item label="Name">
              {props?.company?.name}
            </Descriptions.Item>
          )}
          {props.company?.contactName && props.company.contactName != null && (
            <Descriptions.Item label="Contact Name">
              {`${
                props.company?.contactName?.title
                  ? props.company.contactName?.title
                  : ""
              } ${
                props.company?.contactName?.firstName
                  ? props.company?.contactName?.firstName
                  : ""
              } ${
                props.company.contactName?.lastName
                  ? props.company?.contactName?.lastName
                  : ""
              }`}
            </Descriptions.Item>
          )}
          {props.company?.contactEmailAddress && (
            <Descriptions.Item label="Contact Email address">
              {props.company?.contactEmailAddress}
            </Descriptions.Item>
          )}
          {props.company?.contactPhoneNumber && (
            <Descriptions.Item label="Contact Telephone">
              {props.company.contactPhoneNumber}
            </Descriptions.Item>
          )}
          {props.company?.address && (
            <Descriptions.Item label="Contact Address">
              {props.company.address?.line1}
              {props.company.address?.line2 &&
                `, ${props.company.address?.line2}`}
              {props.company.address?.locality &&
                `, ${props.company.address?.locality}`}
              {props.company.address?.postalCode &&
                `, ${props.company.address?.postalCode}`}
            </Descriptions.Item>
          )}

          <Descriptions.Item label="ABN">
            {props.company?.abn || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="AFSL">
            {props.company?.afsl || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            {props.company?.status}
          </Descriptions.Item>
          {props.company?.parentCompanyID && (
            <Descriptions.Item label="Parent Company">
              {getParentCompany.isLoading
                ? "Loading..."
                : getParentCompany.data?.name || props.company.parentCompanyID}
            </Descriptions.Item>
          )}
          {props.company?.enabledProducts &&
            props.company.enabledProducts.length > 0 && (
              <Descriptions.Item label="Enabled Products">
                {props.company.enabledProducts
                  .map((item) => item.name)
                  .join(", ")}
              </Descriptions.Item>
            )}
        </Descriptions>
      </Card>
      {props.company?.parentCompanyID === null && (
        <>
          <Card
            style={{ marginBottom: "20px" }}
            title="Offices"
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleOpenModal}
              >
                Create Office
              </Button>
            }
          >
            <Table<TableRecord>
              rowKey={(record) => record.id || "empty"}
              size="small"
              dataSource={
                (getChildCompanies.isLoading ||
                getChildCompanies.data === undefined
                  ? []
                  : getChildCompanies.data && getChildCompanies.data.length > 0
                  ? getChildCompanies.data
                  : ([
                      { id: "empty", isEmpty: true }
                    ] as TableRecord[])) as TableRecord[]
              }
              loading={getChildCompanies.isLoading}
              pagination={false}
              columns={[
                {
                  title: "Name",
                  key: "name",
                  onCell: (
                    record: TableRecord
                  ): React.TdHTMLAttributes<HTMLTableCellElement> => {
                    if ("isEmpty" in record && record.isEmpty) {
                      return { colSpan: 0 }
                    }
                    return {}
                  },
                  render: (company: TableRecord) => {
                    if ("isEmpty" in company && company.isEmpty) {
                      return null
                    }
                    const displayCompany = company as IDisplayCompany
                    return (
                      <Link to={`/broker/${displayCompany.id}`}>
                        <u>{displayCompany.name || "-"}</u>
                      </Link>
                    )
                  }
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  key: "status",
                  onCell: (
                    record: TableRecord
                  ): React.TdHTMLAttributes<HTMLTableCellElement> => {
                    if ("isEmpty" in record && record.isEmpty) {
                      return { colSpan: 0 }
                    }
                    return {}
                  },
                  render: (
                    status: string | number | undefined,
                    record: TableRecord
                  ) =>
                    "isEmpty" in record && record.isEmpty
                      ? null
                      : getStatusLabel(status)
                },
                {
                  title: "Contact Email",
                  dataIndex: "contactEmailAddress",
                  key: "contactEmailAddress",
                  onCell: (
                    record: TableRecord
                  ): React.TdHTMLAttributes<HTMLTableCellElement> => {
                    if ("isEmpty" in record && record.isEmpty) {
                      return { colSpan: 0 }
                    }
                    return {}
                  },
                  render: (
                    email: string | null | undefined,
                    record: TableRecord
                  ) =>
                    "isEmpty" in record && record.isEmpty ? null : email ? (
                      <a href={`mailto:${email}`}>{email}</a>
                    ) : (
                      "-"
                    )
                }
              ]}
            />
          </Card>
          <Modal
            title="Create Office"
            visible={isModalVisible}
            onCancel={handleCloseModal}
            footer={null}
            width={800}
          >
            <BrokerForm
              form={form}
              onFinish={handleCreateOffice}
              parentCompany={props.company}
              parentCompanyDisabled={true}
              enabledProductsDisabled={true}
              fieldsetTitle="Office details"
              showCancelButton={true}
              onCancel={handleCloseModal}
              submitButtonText="Create Office"
              submitButtonLoading={createOffice.isLoading}
            />
          </Modal>
        </>
      )}
    </>
  )
}

export interface ISummary {
  company?: IDisplayCompany
  isLoading: boolean
}
